/**
 * Copy for the Journey surface.
 *
 * The backend returns i18n *keys* rather than display strings (`title_key`,
 * `subtitle_key`, `description_key`, `cta_key`, `stage_key`) and no locale
 * bundle exists in either service — same known gap as Explore
 * (docs/explore-implementation-plan.md §3). Strings transcribed from the
 * Journey Figma flow (file YYo0MkKiOOHhv8hvWTXASu, section 9290:10774).
 */

const COPY = {
  "cta.take_the_next_step": "Take the Next Step",
  "cta.understand_this_pattern": "Understand this Pattern",
  "cta.talk_with_aris": "Talk with Aris",
  "focus.understanding_yourself.title": "Understanding Yourself",
  "focus.understanding_yourself.subtitle":
    "Building your foundation through the five Discoveries.",
  "journey.mission.title": "MISSION",
  // Level descriptions (Figma level-detail screens). UI copy about the level
  // concept — descriptive, never a ranking.
  "level.primary.description":
    "These are the patterns that show up most naturally for you — how you tend to lead, connect, and express yourself across situations without effort.",
  "level.active.description":
    "Strengths you use with regularity — patterns you can reach for reliably, even when a situation asks something of you.",
  "level.moderate.description":
    "Patterns that support you in some areas of life — present and usable, though they may take more intention to draw on.",
  "level.subtle.description":
    "Quieter patterns — they may surface in specific situations or with particular people, and can grow with attention.",
};

/** Resolve a backend copy key, falling back to `fallback` then the key itself. */
export const t = (key, fallback) => {
  if (!key) return fallback || "";
  return COPY[key] || fallback || key;
};

/**
 * Today's Check-In card + flow.
 *
 * Transcribed from Journey UX Spec v26.1 §3 ("Today's Check-In"), which names
 * both card states and both CTAs, and plan 08 §11.4 for the ~90-second target
 * and the no-streaks rule. The prompt wording itself never appears here — the
 * backend composes it (Journey §10 pools, core-lambda
 * `presence_module/check_in.py`) and the client renders what it is sent.
 *
 * `submit` is the one string the spec does not supply: a submit control is
 * structurally required and §3 gives only "Start Check-In" / "Check In Again",
 * so it reuses that same verb rather than inventing a new one.
 */
export const CHECK_IN_COPY = {
  eyebrow: "TODAY'S CHECK-IN",
  notCompletedTitle: "Not yet",
  completedTitle: "Completed",
  start: "Start Check-In",
  again: "Check In Again",
  completedAt: (time) => `Completed today at ${time}`,
  completedNote: "You can check in again if something meaningful changes.",
  notCompletedNote: "Your daily check-in is a 90-second way to stay connected.",
  // Flow
  heading: "Today's Check-In",
  followUpsHeading: "Before we talk about today",
  submit: "Check In",
  submitting: "Saving…",
  loading: "Composing today's check-in…",
  loadError:
    "We couldn't open your check-in just now. Close this and try again in a moment.",
  submitError: "We couldn't save your check-in. Try again in a moment.",
  close: "Close check-in",
  scaleHint: "1 = low, 5 = high",
};

/** Journey home section headings (Figma 10905:20092). */
export const JOURNEY_HOME_COPY = {
  title: "Journey",
  subtitle:
    "See where you're going, what matters most right now, and how you're moving toward a healthier, more connected, more fulfilled life.",
  mission: {
    title: "Your Mission",
    subtitle: "The life you're intentionally working to build.",
    cta: "View My Mission",
    evolve: "Evolve My Mission",
  },
  matters: {
    title: "What Matters Most Right Now",
    subtitle: "The areas of your life that deserve the most attention right now.",
    cta: "Review Priorities",
  },
  next: {
    title: "What's Next",
    subtitle: "The most meaningful next step Aris sees for you right now.",
  },
  checkIn: {
    title: "Today's Check-In",
    subtitle: "A few minutes to reflect, follow through, and help Aris understand what's changing.",
    cta: "Check In with Aris",
    resume: "Continue Check-In",
    notYet: "Not yet",
  },
  commitments: {
    title: "Your Commitments",
    subtitle:
      "The things you've committed to that matter most. Aris keeps track and follows up with you",
    listLabel: "Your Commitments",
    seeAll: "See All",
  },
  momentum: {
    title: "Your Momentum",
    subtitle: "See how you're moving toward a healthier, more connected, more fulfilled life.",
  },
  progress: {
    title: "Your Progress",
    subtitle: "Meaningful changes, actions, commitments, and insights along the way.",
    cta: "View More",
  },
};

/** The five Mission dimensions, in the order the design shows the chips (Legacy added
 * 2026-10-15; a Mission written earlier gains it at its next Evolve). */
export const MISSION_DIMENSION_LABEL = {
  identity: "Character",
  contribution: "Contribution",
  relationships: "Relationships",
  way_of_living: "Way of Living",
  legacy: "Legacy",
};

/**
 * Momentum v2 (guide §5.1c). Five member-facing states; NOT_ENOUGH_DATA is
 * internal and renders `why` instead of a label, never the enum. No numbers,
 * rings or bars anywhere.
 */
export const MOMENTUM_DIMENSION_COPY = {
  HEALTHIER: { label: "HEALTHIER", subtitle: "Your mind and body" },
  MORE_CONNECTED: { label: "MORE CONNECTED", subtitle: "Your relationships with others" },
  MORE_FULFILLED: { label: "MORE FULFILLED", subtitle: "Your alignment with your Mission" },
};

export const MOMENTUM_STATE_LABEL = {
  NEEDS_SUPPORT: "Needs Support",
  STRENGTHENING: "Strengthening",
  BUILDING: "Building",
  STRONG: "Strong",
  FLOURISHING: "Flourishing",
};

export const MOMENTUM_DIRECTION_LABEL = {
  IMPROVING: "Improving",
  STEADY: "Steady",
  NEEDS_ATTENTION: "Needs attention",
};

/**
 * What's Next, DISCOVERY shape (§5.1d, §7 J4). The step carries only
 * `discovery_key` / `cta` / `progress`, so its words are client-owned. The
 * design words every Discovery step the same way; a per-key override can be
 * added here without touching the card.
 */
export const WHATS_NEXT_DISCOVERY_COPY = {
  default: {
    title: "Complete Understanding Yourself",
    body: "Finish your remaining discoveries so Aris can deepen its understanding of you.",
  },
};

export const WHATS_NEXT_CTA = {
  CONTINUE_DISCOVERY: "Continue",
  START_DISCOVERY: "Start",
  default: "Continue",
};

/** Commitments (WP-W11; Figma 10905:20092 and 10911:20849). */
export const COMMITMENT_COPY = {
  eyebrow: "YOUR COMMITMENT",
  listTitle: "Your Commitments",
  listSubtitle: "Everything you've committed to, newest first.",
  empty: "You don't have any commitments yet.",
  loadError: "We couldn't load your commitments. Please try again.",
  notFound: "We couldn't find this commitment.",
  completedOn: (date) => `Completed ${date}`,
  dueThisWeek: "This week",
  dueToday: "Today",
  dueTomorrow: "Tomorrow",
  dueOn: (date) => `By ${date}`,
  overdueSince: (date) => `Planned for ${date}`,
  recentActivity: "Recent Activity",
  complete: "Mark as Complete",
  delay: "Not Yet",
  release: "Let It Go",
  actionError: "That didn't go through. Please try again.",
  // Static product copy from Figma 10911:20849 — describes how Aris works,
  // not this commitment, so it needs no payload field.
  trackingTitle: "Aris is keeping track",
  trackingBody:
    "Your progress can be updated through your conversations, daily check-ins, and connected health data when available.",
  talk: "Talk with Aris",
  talkCaption: "Keep the conversation going. I'm here to support you.",
  talkSheetTitle: "Talk with Aris",
  talkSheetSubtitle: "Talk through how this commitment is going.",
  progressTitle: "Progress",
  progressSub: (done, target) => `${done} of ${target} completed`,
  stepLabel: (index, target) => `${index} of ${target}`,
  whyTitle: "Why this matters",
  noteTitle: "A note from Aris",
  minutes: (n) => `${n} ${n === 1 ? "minute" : "minutes"}`,
  // state_history entries, by state. There is no failed state: a commitment
  // not kept is DELAYED or RELEASED, and "missed" is never shown.
  state: {
    CREATED: "Committed",
    PLANNED: "Planned",
    IN_PROGRESS: "In progress",
    COMPLETED: "Completed",
    DELAYED: "Moved to later",
    UPDATED: "Updated",
    RELEASED: "Let go",
  },
};

/** The full Your Progress page (Figma 10880:19687). */
export const PROGRESS_COPY = {
  title: "Your Progress",
  subtitle: "Meaningful changes, actions, commitments, and insights along the way.",
  empty: "Your progress will appear here as things change in your life.",
  loadError: "We couldn't load your progress. Please try again.",
};
